<template>
  <div :class="filtered ? 'container-fluid product-wrapper sidebaron' : 'container-fluid product-wrapper'">
    <div class="product-grid">
      
      <div class="feature-products">
        <BaseFilter
          screenKey="manifest-list-page"
          :model-value="allParams"
          @update:model-value="val => Object.assign(allParams, val)"
          :fields="filterConfig"
          @search="handleSearch"
          @reset="handleReset"
        />
      </div>
      
      <div class="p-4 bg-white mt-3 shadow-sm rounded">
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h3 class="m-0">Manifests List</h3>
          <div class="table-tools d-flex">
             <button class="btn btn-sm btn-outline-secondary me-2" @click="fetchData">
               <i class="fa fa-refresh me-1"></i> Refresh
             </button>
             <button class="btn btn-sm btn-primary" @click="toggleForm">
               <i class="fa fa-plus"></i> Add
             </button>
          </div>
        </div>

        <BaseTable
          storage-key="manifests"
          :columns="tableCols"
          :data="tableData"
          :loading="loading"
          :total-items="total"
          v-model:current-page="allParams.page"
          v-model:page-size="allParams.limit"
          show-checkbox
          @sort="handleSort"
          @selection-change="handleSelection"
        >
          <template #cell(status)="{ item }">
            <span :class="item.status === 1 ? 'badge bg-success' : 'badge bg-secondary'">
              {{ item.status === 1 ? 'Active' : 'Inactive' }}
            </span>
          </template>
          <template #cell(fee)="{ item }">
            {{ Number(item.fee).toLocaleString() }}
          </template>
          <template #cell(note)="{ item }">
            <span v-if="item.note" class="d-inline-block text-truncate" style="max-width: 80px;" :title="item.note">
              {{ item.note }}
            </span>
          </template>
          <template #cell(created_at)="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template #actions="{ item }">
            <button class="btn btn-sm btn-primary me-1" @click="editUser(item)">
              <i class="fa fa-edit"></i>
            </button>
            <button class="btn btn-sm btn-danger" @click="deleteUser(item)">
              <i class="fa fa-trash"></i>
            </button>
          </template>
        </BaseTable>
      </div>
    </div>
    <Form 
      :isOpen="isShowForm" 
      :itemData="selectedItem"  @close="isShowForm = false" 
      @save="handleSaveData"
    />
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue';

import BaseFilter from "@/components/common/BaseFilter.vue";
import BaseTable from "@/components/common/BaseTable.vue";
import { useManifest } from "@/composables/useManifest";
import Form from './Form.vue'
import { createToaster } from "@meforma/vue-toaster";

const toaster = createToaster({
  position: "top-right",
  duration: 2000,
});
const { fetchManifestApi, createManifestApi, updateManifestApi } = useManifest();
const filtered = ref(false);
const loading = ref(false);
const tableData = ref([]);
const total = ref(0);
const isShowForm = ref(false)
const selectedItem = ref(null);

const allParams = reactive({
  page: 1,
  limit: 10,
  orderBy: 'id',
  sortedBy: 'desc',
  order: '',
  searchJoin: 'AND',
  include: 'manifest',
  searchFields: '',
  search: '',
});

// Cấu hình các ô nhập của Filter
const filterConfig = [
  {
    key: 'name',
    type: 'text',
    label: 'Name',
    operator: 'like',
    placeholder: 'Name...',
    isDefault: true
  },
  {
    key: 'code',
    type: 'text',
    operator: 'like',
    label: 'Code',
    placeholder: 'Code',
    isDefault: true
  }
];

// Cấu hình các cột của Bảng
const tableCols = [
  { key: 'id', label: 'ID', width: '70px', sortable: true },
  { key: 'name', label: 'Name', sortable: true },
  { key: 'code', label: 'Code', sortable: true },
  { key: 'fee', label: 'Fee', align: 'center' },
  { key: 'weight', label: 'Weight', align: 'center' },
  { key: 'status', label: 'Status', align: 'center' },
  { key: 'note', label: 'Note' },
  { key: 'created_at', label: 'Created At' },
];

const fetchData = async (resetPage = false) => {
  if (resetPage) {
    allParams.page = 1;
  }

  loading.value = true;

  try {
    const res = await fetchManifestApi({ ...allParams });

    if (res.success && res.data) {
      tableData.value = res.data.data || [];

      total.value = res.data.meta?.pagination?.total || 0;
    } else {
      toaster.error(res.error || "Không thể tải dữ liệu");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleReset = () => {
  allParams.search = '';
  allParams.searchFields = '';
  
  if (allParams.page === 1) fetchData();
  else allParams.page = 1;
};

const handleSort = ({ key, order }) => {
  allParams.orderBy = key;
  allParams.order = order;
  fetchData();
};

const handleSelection = (ids) => {
  console.log("IDs đã chọn:", ids);
};

watch(() => [allParams.page, allParams.limit], () => fetchData());

onMounted(fetchData);

const toggleForm = () => {
  selectedItem.value = null;
  isShowForm.value = true;
};

const handleSaveData = async (data) => {
  try {
    let res;
    if (data.id) {
      res = await updateManifestApi(data.id, data);
    } else {
      res = await createManifestApi(data);
    }

    if (res.success) {
      toaster.success(data.id ? "Cập nhật thành công!" : "Thêm mới thành công!");
      isShowForm.value = false; 
      fetchData();
    } else {
      toaster.error(res.error || "Thao tác thất bại");
    }
  } catch (err) {
    toaster.error("Có lỗi xảy ra trong quá trình kết nối");
  }
};

const editUser = (user) => {
  selectedItem.value = { ...user };
  isShowForm.value = true;
};
const deleteUser = (user) => {
  if(confirm('Xóa ID ' + user.id + '?')) {
    alert('Đã xóa!');
  }
};
const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);

  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();

  const h = String(date.getHours()).padStart(2, '0');
  const i = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');

  return `${d}/${m}/${y} ${h}:${i}:${s}`;
};
</script>

<style scoped>
.filter-section .left-filter {
  max-height: 400px;
  overflow-y: auto;
  z-index: 99 !important;
}
.product-wrapper {
  padding-bottom: 50px;
}
</style>